import { CONFIG } from "../../../config";
import axios from "axios";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";

// CRM base host configuration
const CRMCONFIG = CONFIG.modules["crm"];
const API_HOST_CRM = CRMCONFIG.host;

const isDevelopment = process.env.NODE_ENV === 'development';
const devLog = (message, data) => {
    if (isDevelopment) console.log(message, data);
};

/**
 * useGetCalls
 *
 * Hook to fetch calls list/data from the CRM service.
 * Mirrors structure of analytics hook for consistency (debug logging, query key derivation, defensive inputs).
 *
 * @param {Object} [queries={}] - Query parameters accepted by backend (page, limit, fromDate, toDate, agentId, status, etc.)
 * @param {Object} [options={}] - React Query overrides.
 * @returns {Object} React Query result object.
 */
export const useGetCalls = (queries = {}, options = {}) => {
    const validatedQueries = useMemo(() => {
        if (!queries || typeof queries !== 'object') {
            devLog('[useGetCalls] Invalid queries parameter, using empty object');
            return {};
        }
        return queries;
    }, [queries]);

    const queryOptions = useMemo(() => ({
        enabled: true,
        refetchOnWindowFocus: false,
        retry: 2,
        staleTime: 1000 * 60 * 2, // shorter default staleness for frequently changing list
        cacheTime: 1000 * 60 * 5,
        ...options
    }), [options]);

    const queryKey = useMemo(() => {
        const sortedEntries = Object.entries(validatedQueries)
            .filter(([, value]) => value !== undefined && value !== null)
            .sort(([a], [b]) => a.localeCompare(b));

        return ["calls", ...sortedEntries.flat()];
    }, [validatedQueries]);

    const fetchCalls = useCallback(async () => {
        try {
            const cleanParams = Object.fromEntries(
                Object.entries(validatedQueries).filter(([, value]) => value !== undefined && value !== null && value !== '')
            );

            devLog('[useGetCalls] Calling API with params:', cleanParams);

            const response = await axios.get(`${API_HOST_CRM}/calls`, { params: cleanParams, timeout: 30000 });

            devLog('[useGetCalls] Response:', response.data);

            if (!response.data) throw new Error('No data received from server');
            return response.data;
        } catch (error) {
            const isNetworkError = !error.response && error.code === 'ENOTFOUND';
            const isTimeout = error.code === 'ECONNABORTED';
            let msg = 'Error fetching calls';
            if (isNetworkError) msg += ': Network error';
            else if (isTimeout) msg += ': Timeout';
            else if (error.response) msg += `: Server responded with ${error.response.status}`;

            console.error(msg, { error, params: validatedQueries, time: new Date().toISOString() });
            throw new Error(msg);
        }
    }, [validatedQueries]);

    return useQuery({
        queryKey,
        queryFn: fetchCalls,
        enabled: queryOptions.enabled,
        refetchOnWindowFocus: queryOptions.refetchOnWindowFocus,
        retry: queryOptions.retry,
        staleTime: queryOptions.staleTime,
        cacheTime: queryOptions.cacheTime,
        onError: (error) => console.error('useGetCalls error', { error: error.message, queries: validatedQueries }),
        select: (data) => data || null,
    });
};
